import { useMemo } from 'react';
import '../styles/star-animations.css';

interface StarFieldProps {
  /** Number of stars to render */
  starCount?: number;
  /** Additional CSS class for the container */
  containerClassName?: string;
  /** Additional CSS class for individual stars */
  starClassName?: string;
}

interface StarConfig {
  id: number;
  left: number;
  top: number;
  size: number;
  opacity: number;
  duration: number;
  delay: number;
  twinkleDelay: number;
  twinkleDuration: number;
  floatVariant: string;
}

export default function StarField({
  starCount = 75,
  containerClassName = '',
  starClassName = '',
}: StarFieldProps) {
  // Memoize star configurations - create stars with random positions and properties
  const starConfigs = useMemo<StarConfig[]>(() => {
    const floatVariants = ['float-1', 'float-2', 'float-3', 'float-4', 'float-5', 'float-6'];
    return Array.from({ length: starCount }, (_, i) => ({
      id: i,
      left: Math.random() * 100, // 0-100% of viewport width
      top: Math.random() * 100, // 0-100% of viewport height
      size: 1 + Math.random() * 3, // 1-4px diameter
      opacity: 0.3 + Math.random() * 0.7, // 0.3-1.0 opacity
      duration: 8 + Math.random() * 12, // 8-20s animation duration
      delay: Math.random() * -5, // Random start offset
      twinkleDelay: Math.random() * 3, // Delay for twinkle animation
      twinkleDuration: 2 + Math.random() * 3, // 2-5s twinkle duration
      floatVariant: floatVariants[Math.floor(Math.random() * floatVariants.length)], // Random float pattern
    }));
  }, [starCount]);

  return (
    <div className={`star-field ${containerClassName}`}>
      {starConfigs.map(star => (
        <div
          key={star.id}
          className={`star-wrapper ${starClassName}`}
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            opacity: star.opacity,
          }}
        >
          <div
            className="star"
            style={{
              width: `${star.size}px`,
              height: `${star.size}px`,
              animationName: `star-${star.floatVariant}, star-twinkle`,
              animationDuration: `${star.duration}s, ${star.twinkleDuration}s`,
              animationTimingFunction: 'ease-in-out, ease-in-out',
              animationIterationCount: 'infinite, infinite',
              animationDelay: `${star.delay}s, ${star.twinkleDelay}s`,
              animationFillMode: 'none, none',
            }}
          />
        </div>
      ))}
    </div>
  );
}
